import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  extractMsg91AccessToken,
  invokeMsg91DefaultUi,
  loadMsg91Sdk,
} from "@/lib/msg91Sdk";
import {
  appendDiagnosticStatus,
  OTP_DIAGNOSTIC_STATUS,
  statusForSdkEvent,
} from "@/lib/otpDiagnostics";

export const OTP_FAILED_MESSAGE = "Mobile verification failed. Please try again.";

const sdkMessages = {
  SDK_LOAD_ERROR: "The secure verification service could not be loaded. Please try again.",
  SDK_LOAD_TIMEOUT: "Secure verification took too long to open. Please try again.",
  SDK_API_MISSING: "Secure verification is unavailable. Please try again later.",
  SDK_INITIALIZATION_IN_PROGRESS: "Secure verification is already opening.",
};

export const useOtpVerification = () => {
  const [config, setConfig] = useState({ enabled: false, mode: "off", required: true });
  const [verified, setVerified] = useState(false);
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [liveError, setLiveError] = useState("");
  const [configError, setConfigError] = useState(false);
  const [prepared, setPrepared] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [lifecycleStatus, setLifecycleStatus] = useState(OTP_DIAGNOSTIC_STATUS.IDLE);
  const [lifecycleHistory, setLifecycleHistory] = useState([OTP_DIAGNOSTIC_STATUS.IDLE]);
  const liveInitializationActive = useRef(false);

  const reportLifecycle = useCallback((status) => {
    setLifecycleStatus(status);
    setLifecycleHistory((history) => appendDiagnosticStatus(history, status));
  }, []);

  const reportSdkEvent = useCallback((event) => {
    const status = statusForSdkEvent(event);
    if (status) reportLifecycle(status);
  }, [reportLifecycle]);

  useEffect(() => {
    api.get("/otp/config")
      .then(({ data }) => {
        setConfig(data);
        setConfigError(false);
        reportLifecycle(
          data.mode === "preview_mock"
            ? OTP_DIAGNOSTIC_STATUS.PREVIEW_MOCK_ACTIVE
            : OTP_DIAGNOSTIC_STATUS.IDLE
        );
      })
      .catch(() => {
        setConfig({ enabled: false, mode: "off", required: true });
        setConfigError(true);
      })
      .finally(() => setLoading(false));
  }, [reportLifecycle]);

  const prepare = useCallback(async (payload) => {
    if (!config.enabled || verified || busy || preparing || prepared) return prepared;
    const tokenAuth = process.env.REACT_APP_MSG91_WIDGET_TOKEN;
    setPreparing(true);
    setLiveError("");
    reportLifecycle(OTP_DIAGNOSTIC_STATUS.ACTION_PREPARING);
    try {
      await api.post("/otp/actions", payload);
      if (config.mode === "live") {
        if (!tokenAuth || !config.widget_id) {
          throw new Error("Phone verification is temporarily unavailable.");
        }
        reportLifecycle(OTP_DIAGNOSTIC_STATUS.SCRIPT_LOADING);
        await loadMsg91Sdk(reportSdkEvent);
      } else if (config.mode === "preview_mock") {
        reportLifecycle(OTP_DIAGNOSTIC_STATUS.PREVIEW_MOCK_READY);
      }
      setPrepared(true);
      return true;
    } catch (error) {
      const message = sdkMessages[error?.message] || "Verification could not be prepared.";
      setLiveError(OTP_FAILED_MESSAGE);
      reportLifecycle(OTP_DIAGNOSTIC_STATUS.INTEGRATION_ERROR);
      throw new Error(message);
    } finally {
      setPreparing(false);
    }
  }, [busy, config, prepared, preparing, reportLifecycle, reportSdkEvent, verified]);

  const begin = async (payload) => {
    if (!config.enabled || verified || busy) return false;
    if (liveInitializationActive.current) {
      reportLifecycle(OTP_DIAGNOSTIC_STATUS.INITIALIZATION_BLOCKED);
      return false;
    }
    const tokenAuth = process.env.REACT_APP_MSG91_WIDGET_TOKEN;
    if (config.mode === "preview_mock") {
      if (!prepared) {
        await prepare(payload);
      }
      setRequested(true);
      return true;
    }
    if (!prepared) {
      throw new Error("Verification is still preparing. Please wait a moment.");
    }

    setBusy(true);
    setLiveError("");
    setRequested(true);
    liveInitializationActive.current = true;
    try {
      invokeMsg91DefaultUi({
        widgetId: config.widget_id,
        tokenAuth,
        identifier: `91${payload.mobile}`,
        report: reportSdkEvent,
        onSuccess: async (data) => {
          liveInitializationActive.current = false;
          let accessToken = null;
          try {
            accessToken = extractMsg91AccessToken(data);
          } catch {
            accessToken = null;
          }
          if (!accessToken) {
            setLiveError(OTP_FAILED_MESSAGE);
            reportLifecycle(OTP_DIAGNOSTIC_STATUS.CALLBACK_TOKEN_MISSING);
            return;
          }
          try {
            reportLifecycle(OTP_DIAGNOSTIC_STATUS.ACCESS_TOKEN_HANDOFF);
            const { data: verification } = await api.post("/otp/verify", {
              access_token: accessToken,
            });
            if (verification?.verified === true) {
              setVerified(true);
              setRequested(false);
              reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_VERIFIED);
              toast.success("Mobile number verified successfully.");
              return;
            }
            setLiveError(OTP_FAILED_MESSAGE);
            reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_REJECTED);
          } catch {
            setLiveError(OTP_FAILED_MESSAGE);
            reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_REJECTED);
          }
        },
        onFailure: () => {
          liveInitializationActive.current = false;
          setLiveError(OTP_FAILED_MESSAGE);
        },
        onTimeout: () => {
          liveInitializationActive.current = false;
          setLiveError(OTP_FAILED_MESSAGE);
          reportLifecycle(OTP_DIAGNOSTIC_STATUS.CALLBACK_TIMEOUT);
        },
      });
      return true;
    } catch (error) {
      liveInitializationActive.current = false;
      setRequested(false);
      const message = sdkMessages[error?.message] || "Secure verification could not be opened.";
      setLiveError(OTP_FAILED_MESSAGE);
      reportLifecycle(OTP_DIAGNOSTIC_STATUS.INTEGRATION_ERROR);
      throw new Error(message);
    } finally {
      setBusy(false);
    }
  };

  const verifyMockCode = async (code) => {
    if (config.mode !== "preview_mock" || busy) return false;
    setBusy(true);
    try {
      const { data: verification } = await api.post("/otp/verify", { mock_code: code });
      if (verification?.verified !== true) {
        reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_REJECTED);
        throw new Error("The verification code is not correct. Please try again.");
      }
      setVerified(true);
      setRequested(false);
      reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_VERIFIED);
      return true;
    } catch (error) {
      reportLifecycle(OTP_DIAGNOSTIC_STATUS.BACKEND_REJECTED);
      throw error;
    } finally {
      setBusy(false);
    }
  };

  const reset = useCallback(() => {
    liveInitializationActive.current = false;
    setVerified(false);
    setRequested(false);
    setLiveError("");
    setPrepared(false);
    setPreparing(false);
    reportLifecycle(
      config.mode === "preview_mock"
        ? OTP_DIAGNOSTIC_STATUS.PREVIEW_MOCK_ACTIVE
        : OTP_DIAGNOSTIC_STATUS.IDLE
    );
  }, [config.mode, reportLifecycle]);
  return {
    enabled: config.enabled,
    mode: config.mode,
    required: config.required === true,
    configError,
    verified,
    canProceed: verified,
    requested,
    loading,
    busy,
    liveError,
    begin,
    prepare,
    prepared,
    preparing,
    lifecycleStatus,
    lifecycleHistory,
    verifyMockCode,
    reset,
  };
};